import {
  explicitCitationPageFromMedia,
  getSecondsFromTimeString,
  resolveMediaPlaybackObjectUrl,
} from '@/shared/functions';
import type { MediaSource, Slide } from '@/shared/types';
import { isExplicitlyClearedCapture, resolvePersistedCaptureRef } from './captureRegion';
import { storageUrlForDisplay } from './storageUrlForDisplay';
import {
  captureVideoFrameDataUrl,
  uploadUserImage,
  type UserImageUploadPurpose,
} from '@/shared/userImageStorage';

const DEFAULT_VIDEO_SEEK_SECONDS = 1;
const VIDEO_THUMBNAIL_WIDTH_PX = 320;

const isDataUrlImage = (value: unknown): value is string =>
  typeof value === 'string' && value.startsWith('data:image/');

const isNonEmptyImageRef = (value: unknown): value is string =>
  typeof value === 'string' && value.trim() !== '';

/** Same precedence as the media player compact still-frame lookup. */
export function getStillFrameImage(
  mediaItem: MediaSource | null | undefined,
): string | undefined {
  for (const context of [mediaItem?.mediaContext, mediaItem?.sourceContext]) {
    const still = stillFrameFromContext(context);
    if (still) return still;
  }
  return undefined;
}

function pdfCanvasFromContext(
  context: MediaSource['mediaContext'] | MediaSource['sourceContext'] | null | undefined,
): string | undefined {
  const image = context?.canvas?.image;
  return isNonEmptyImageRef(image) ? image.trim() : undefined;
}

function stillFrameFromContext(
  context: MediaSource['mediaContext'] | MediaSource['sourceContext'] | null | undefined,
): string | undefined {
  if (isDataUrlImage(context?.stillFrame)) {
    return context.stillFrame;
  }
  if (isDataUrlImage(context?.canvas?.stillFrameImage)) {
    return context.canvas.stillFrameImage;
  }
  const storedRef = context?.canvas?.stillFrameImage;
  return isNonEmptyImageRef(storedRef) ? storedRef.trim() : undefined;
}

function getPdfCanvasImage(media: MediaSource | null | undefined): string | undefined {
  for (const context of [media?.mediaContext, media?.sourceContext]) {
    const image = pdfCanvasFromContext(context);
    if (image) return image;
  }
  return undefined;
}

export type ThumbnailMediaKind = 'video' | 'pdf';

export function thumbnailMediaKind(
  media: MediaSource | null | undefined,
): ThumbnailMediaKind | null {
  const type = media?.mediaType;
  if (type === 'video') return 'video';
  if (type === 'pdf' || type === 'docx' || type === 'pptx') return 'pdf';
  return null;
}

/**
 * True when compact timeline views would show the "Add Preview" / PDF-icon
 * placeholder instead of a captured still. Images render themselves.
 */
export function eventMediaNeedsThumbnail(
  media: MediaSource | null | undefined,
): boolean {
  const kind = thumbnailMediaKind(media);
  if (kind === 'video') {
    return !getStillFrameImage(media);
  }
  if (kind === 'pdf') {
    return !getPdfCanvasImage(media);
  }
  return false;
}

/**
 * True when a Displayed Media card can paint a real still / image preview.
 * Types without one should show a type icon on the card chrome — not a nested
 * viewer with its own dark background.
 */
export function mediaHasCompactPreviewImage(
  media: MediaSource | null | undefined,
): boolean {
  if (!media) return false;
  if (media.mediaType === 'youtube') {
    return Boolean((media.mediaSrc || media.mediaUrl || '').trim());
  }
  return Boolean(getCompactPreviewDisplayUrl(media));
}

/**
 * What a compact preview URL actually points at.
 *
 * ``derived-still`` is a saved thumbnail (capture crop, video still-frame, or
 * PDF page canvas) that was rasterized at its final orientation. ``source-image``
 * is the original image file, which still needs the saved/temporary rotation
 * applied when painted. Callers that transform the painted image must branch on
 * this rather than on "is a compact preview showing" — see TR-2965.
 */
export type CompactPreviewKind = 'none' | 'derived-still' | 'source-image';

export type CompactPreview = {
  url: string;
  kind: CompactPreviewKind;
};

const NO_COMPACT_PREVIEW: CompactPreview = { url: '', kind: 'none' };

/**
 * Compact preview URL plus what it points at. Prefer the saved thumbnail
 * (`capture` / still-frame / PDF canvas image) over the source file so PDFs
 * and A/V do not nest a full viewer in the 140px tile.
 *
 * Single source of truth for the precedence; {@link getCompactPreviewDisplayUrl}
 * is a thin wrapper so callers that only need the URL stay unchanged.
 */
export type CompactPreviewOptions = {
  /**
   * Read a capture only from this row's ``mediaContext``. Shared
   * ``sourceContext.capture`` is a source-level fallback and would paint one
   * event's crop on every card that cites the same file (TR-4168).
   */
  ownCaptureOnly?: boolean;
};

export function resolveCompactPreview(
  media: MediaSource | null | undefined,
  options?: CompactPreviewOptions,
): CompactPreview {
  if (!media) return NO_COMPACT_PREVIEW;
  const sourceImage = (): CompactPreview => {
    if (media.mediaType !== 'image') return NO_COMPACT_PREVIEW;
    const src = (media.srcToUse || media.mediaSrc || media.mediaUrl || '').trim();
    if (!src) return NO_COMPACT_PREVIEW;
    return { url: storageUrlForDisplay(src), kind: 'source-image' };
  };

  // Use Full Media writes ``capture: ''``. Do not resurrect sourceContext.capture
  // or leftover canvas stills on compact tiles.
  if (isExplicitlyClearedCapture(media)) {
    return sourceImage();
  }
  const sharedCapture = options?.ownCaptureOnly
    ? ''
    : (isNonEmptyImageRef(media.sourceContext?.capture)
      ? media.sourceContext.capture.trim()
      : '');
  const capture = resolvePersistedCaptureRef(media) || sharedCapture;
  if (capture) return { url: storageUrlForDisplay(capture), kind: 'derived-still' };
  const still = options?.ownCaptureOnly
    ? stillFrameFromContext(media.mediaContext)
    : getStillFrameImage(media);
  if (still) return { url: storageUrlForDisplay(still), kind: 'derived-still' };
  const pdfCanvas = options?.ownCaptureOnly
    ? pdfCanvasFromContext(media.mediaContext)
    : getPdfCanvasImage(media);
  if (pdfCanvas) return { url: storageUrlForDisplay(pdfCanvas), kind: 'derived-still' };
  return sourceImage();
}

export function getCompactPreviewDisplayUrl(
  media: MediaSource | null | undefined,
): string {
  return resolveCompactPreview(media).url;
}

/**
 * Degrees to rotate a compact image at paint time.
 *
 * A ``source-image`` URL is the original file, so the saved
 * ``mediaContext.rotation`` still has to be applied. A ``derived-still``
 * (capture crop, video still, PDF canvas) was rasterized already rotated.
 */
export function resolveCompactPreviewRotation(
  media: MediaSource | null | undefined,
  options?: CompactPreviewOptions,
): number {
  if (resolveCompactPreview(media, options).kind !== 'source-image') return 0;
  const raw = media?.mediaContext?.rotation;
  const degrees = typeof raw === 'number' && Number.isFinite(raw) ? raw : 0;
  return ((degrees % 360) + 360) % 360;
}

export type CompactCaptureStillArgs = {
  hasCompactPreview: boolean;
  mediaType?: string | null;
  isThumbnailMode: boolean;
  isPresentation: boolean;
  isSourceCard: boolean;
  isFullscreen: boolean;
  /** True when the Full Media | Thumbnail switcher is on this surface. */
  captureSwitcherShown: boolean;
  viewCapture: boolean;
};

/**
 * Whether MediaDisplay should paint the compact tile still (bookmark
 * `canvas.image`, video still-frame, or saved capture) instead of the original
 * viewer.
 *
 * That still is only for small tiles: Displayed Media, accordion, and table.
 * Larger surfaces (presentation MediaPane, Evidence Preview, fullscreen) never
 * paint `canvas.image`. They show `mediaContext.capture` via the capture-view
 * path, or the actual document/media.
 */
export function shouldShowCompactCaptureStill(
  args: CompactCaptureStillArgs,
): boolean {
  if (!args.hasCompactPreview || args.mediaType === 'video') return false;
  return args.isThumbnailMode;
}

/**
 * First displayed-media row on a slide, including the legacy top-level
 * ``mediaSrc`` / ``mediaType`` fields used by the presentation sidebar.
 */
export function resolveSlideAccordionMedia(
  slide: Pick<Slide, 'media' | 'mediaSrc' | 'mediaType'> | null | undefined,
): MediaSource | undefined {
  if (!slide) return undefined;
  if (slide.media?.[0]) return slide.media[0];
  if (slide.mediaSrc || slide.mediaType) {
    return {
      mediaType: slide.mediaType,
      mediaSrc: slide.mediaSrc,
      mediaUrl: slide.mediaSrc,
    };
  }
  return undefined;
}

export function thumbnailBackfillKey(
  eventId: string,
  mediaIndex: number,
  media: MediaSource,
): string {
  const src = media.mediaSrc || media.mediaUrl || media.sourceUrl || '';
  return `${eventId}:${mediaIndex}:${src}`;
}

/**
 * Seek target for an auto still frame: citation / trim start when present,
 * otherwise 1s so we don't capture a black first frame.
 */
export function captureSeekSeconds(media: MediaSource): number {
  const candidates = [
    media.mediaContext?.stillFrame,
    media.mediaContext?.start,
    media.mediaContext?.startTime,
    media.sourceContext?.stillFrame,
    media.sourceContext?.start,
    media.sourceContext?.startTime,
  ];
  for (const candidate of candidates) {
    if (candidate === undefined || candidate === null || candidate === '') {
      continue;
    }
    if (typeof candidate !== 'string' && typeof candidate !== 'number') {
      continue;
    }
    const seconds = getSecondsFromTimeString(candidate);
    if (Number.isFinite(seconds) && seconds > 0) {
      return seconds;
    }
  }
  return DEFAULT_VIDEO_SEEK_SECONDS;
}

export async function generateVideoThumbnail(
  media: MediaSource,
): Promise<string | null> {
  const objectUrl = resolveMediaPlaybackObjectUrl(media);
  if (!objectUrl) return null;
  const timeSeconds = captureSeekSeconds(media);
  try {
    return await captureVideoFrameDataUrl(
      objectUrl,
      timeSeconds,
      VIDEO_THUMBNAIL_WIDTH_PX,
    );
  } catch (error) {
    console.warn(
      'Failed to auto-capture video thumbnail:',
      error instanceof Error ? error.message : error,
    );
    return null;
  }
}

export type UploadThumbnailParams = {
  imageData: string;
  purpose: Extract<UserImageUploadPurpose, 'video-still-frame' | 'pdf-page-thumbnail'>;
  caseId: string;
  cellId: string;
  accessToken: string;
  tenantFriendlyName?: string;
  sourceKey?: string;
  sourceMd5?: string;
};

/** Upload to S3; fall back to the embedded data URL when upload fails. */
export async function uploadThumbnail(params: UploadThumbnailParams): Promise<string> {
  try {
    const { s3Uri } = await uploadUserImage({
      imageData: params.imageData,
      mimeType: 'image/webp',
      purpose: params.purpose,
      caseId: params.caseId,
      tenantFriendlyName: params.tenantFriendlyName,
      sourceKey: params.sourceKey,
      sourceMd5: params.sourceMd5,
      cellId: params.cellId,
      accessToken: params.accessToken,
    });
    return s3Uri;
  } catch (error) {
    console.error('Failed to upload auto-generated thumbnail', error);
    return params.imageData;
  }
}

export function applyThumbnailToSlide(
  slide: Slide,
  mediaIndex: number,
  imageRef: string,
  kind: ThumbnailMediaKind,
  extras?: { stillFrameTime?: string; pageNumber?: number },
): Slide {
  const mediaList = [...(slide.media ?? [])];
  const current = mediaList[mediaIndex];
  if (!current) return slide;

  if (kind === 'video') {
    const stillFrameTime =
      extras?.stillFrameTime ?? String(captureSeekSeconds(current));
    mediaList[mediaIndex] = {
      ...current,
      mediaContext: {
        ...current.mediaContext,
        stillFrame: stillFrameTime,
        canvas: {
          ...current.mediaContext?.canvas,
          stillFrameImage: imageRef,
          stillFrame: stillFrameTime,
        },
      },
    };
  } else {
    const pageNumber = extras?.pageNumber ?? explicitCitationPageFromMedia(current);
    mediaList[mediaIndex] = {
      ...current,
      mediaContext: {
        ...current.mediaContext,
        ...(pageNumber != null ? { pageNumber } : {}),
        canvas: {
          ...current.mediaContext?.canvas,
          ...(pageNumber != null ? { pageNumber } : {}),
          image: imageRef,
        },
      },
    };
  }

  return { ...slide, media: mediaList };
}

export function applyPdfThumbnailToMedia(
  media: MediaSource,
  imageRef: string,
  pageNumber?: number,
): MediaSource {
  return {
    ...media,
    mediaContext: {
      ...media.mediaContext,
      ...(pageNumber != null ? { pageNumber } : {}),
      canvas: {
        ...media.mediaContext?.canvas,
        ...(pageNumber != null ? { pageNumber } : {}),
        image: imageRef,
      },
    },
  };
}

export const MAX_THUMBNAIL_BACKFILLS_PER_LOAD = 40;
